<template>
  <div class='inventory-importer'>
    <b-button @click='$refs.modal.show()' variant='link'>
      <font-awesome-icon icon="fa-regular fa-upload" />
      <span>Import</span>
    </b-button>
    <b-modal
      ref='modal'
      class='stock-importer-modal'
      centered
      title='Import Stock'
      size=lg
      :ok-title='ok_title'
      :ok-disabled='!can_start'
      :no-close-on-backdrop='running'
      :no-close-on-esc='running'
      :hide-header-close='running'
      :cancel-disabled='running'
      @ok='handle_ok'
      @show='reset'
      cancel-variant='medium-dark'
      >
      <b-form-file
        v-if='!job'
        v-model="file"
        :state="file_state"
        placeholder="Choose a file or drop it here..."
        drop-placeholder="Drop file here..."
        :disabled='running'
        />

      <b-alert :show='!!error' variant='danger' class='mt-3 mb-0'>{{ error }}</b-alert>

      <div v-if='job' class='mt-3'>
        <b-progress :max='total' height='1.25rem' :animated='running'>
          <b-progress-bar :value='applied + unchanged' variant='success' />
          <b-progress-bar :value='failed' variant='danger' />
        </b-progress>

        <p class='mt-2 mb-0'>
          <span>{{ processed }} of {{ total }} rows</span>
          <span class='text-success ml-2'>{{ applied }} updated</span>
          <span class='text-muted ml-2'>{{ unchanged }} already correct</span>
          <span v-if='failed' class='text-danger ml-2'>{{ failed }} failed</span>
        </p>

        <b-alert :show='complete && !failed && !errors.length' variant='success' class='mt-3 mb-0'>
          Import complete. QuickBooks and WooCommerce both hold the counted quantities.
        </b-alert>
      </div>

      <div v-if='errors.length' class='mt-3'>
        <h6 class='mb-1'>Rows not imported</h6>
        <p class='text-muted small mb-2'>
          These were left alone in both systems. Nothing partial was written for them.
        </p>
        <div class='import-errors'>
          <b-table
            :items='errors'
            :fields='error_fields'
            small
            striped
            responsive
            />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: 'InventoryImporter',
  data () {
    return {
      file: null,
      running: false,
      job: null,
      total: 0,
      processed: 0,
      applied: 0,
      unchanged: 0,
      failed: 0,
      complete: false,
      errors: [],
      error_fields: [
        { key: 'line', label: 'Row' },
        { key: 'sku', label: 'SKU' },
        { key: 'product_id', label: 'Product' },
        { key: 'reason', label: 'Reason' },
      ],
      error: null,
    }
  },
  computed: {

    file_state () {
      return this.file ? true : null;
    },

    can_start () {
      // Once a job has finished the primary button just closes the modal.
      return this.complete || ( !!this.file && !this.running );
    },

    ok_title () {
      if ( this.complete ) return 'Done';
      return this.running ? 'Importing...' : 'Import';
    },

  },
  methods: {

    reset () {
      this.file = null;
      this.running = false;
      this.job = null;
      this.total = 0;
      this.processed = 0;
      this.applied = 0;
      this.unchanged = 0;
      this.failed = 0;
      this.complete = false;
      this.errors = [];
      this.error = null;
    },

    handle_ok ( e ) {
      if ( this.complete ) {
        // Let the modal close, and refresh the list behind it.
        this.$emit( 'import-complete' );
        return;
      }

      e.preventDefault();
      this.start();
    },

    apply_progress ( data ) {
      this.job = data.job;
      this.total = data.total;
      this.processed = data.processed;
      this.applied = data.applied;
      this.unchanged = data.unchanged;
      this.failed = data.failed;
      this.complete = data.complete;
      this.errors = data.errors || [];
    },

    message_for ( err ) {
      const data = err && err.response ? err.response.data : null;
      if ( data && data.message ) return data.message;
      if ( err && err.message ) return err.message;
      return 'The import failed.';
    },

    async start () {
      this.running = true;
      this.error = null;

      let form_data = new FormData();
      form_data.append( 'file', this.file );

      try {
        // Prepare validates the whole sheet and writes nothing, so a bad file fails here
        // rather than part way through.
        const { data } = await axios.post( '/inventory/import', form_data );
        this.apply_progress( data );

        // Each call is one QuickBooks adjustment for a chunk of rows. Sequential on
        // purpose: parallel calls would race the same job transient.
        while ( !this.complete ) {
          const next = await axios.post( `/inventory/import/${this.job}/process` );
          this.apply_progress( next.data );
        }

        this.$emit( 'import-complete' );
      } catch ( err ) {
        this.error = this.message_for( err );
      } finally {
        this.running = false;
      }
    },

  }
}
</script>

<style scoped>
.import-errors {
  max-height: 14rem;
  overflow-y: auto;
}
</style>
